import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import Webcam, { WebcamProps } from "react-webcam";
import useGetLocker from "hooks/useGetLocker";
import useIdleTimer from "hooks/useIdleTimer";
import { useLocation } from "react-router-dom";
import * as Sentry from "@sentry/react";
import { getConfig } from "config";
import { addBreadcrumb, recordIncrement } from "utils/observability";

type MediaError = string | DOMException | Error | undefined;

interface CameraContextProps {
  capture: () => string | null | undefined;
  mediaError: MediaError;
  isEnabled: boolean;
  cameraAvailable: boolean;
}

const CameraContext = createContext<CameraContextProps | null>(null);

const WEBCAM_CONFIG = {
  screenshotFormat: "image/jpeg",
  muted: false,
  audio: false,
  videoConstraints: {
    frameRate: 10,
  },
  style: { position: "absolute", right: "100%" } as const,
} as WebcamProps;

const CAMERA_IDLE_TIMEOUT = 30000; // 30 seconds
const CAMERA_RETRY_TIMEOUT = 60000; // 60 seconds
const MISSING_CAMERA_ERROR_NAME = "NotFoundError";

const getCameraErrorName = (error: MediaError) => {
  const isDomException = typeof DOMException !== "undefined" && error instanceof DOMException;

  if (isDomException || error instanceof Error) {
    return error.name;
  }

  return typeof error === "string" && error.length > 0 ? error : "unknown";
};

const isMissingCameraError = (error: MediaError) =>
  getCameraErrorName(error) === MISSING_CAMERA_ERROR_NAME;

export function CameraProvider({ children }: { children: React.ReactNode }) {
  const webcamRef = useRef<Webcam & HTMLVideoElement>(null);
  const reportedMissingCameraRef = useRef(false);
  const cameraRetryRef = useRef<number>();
  const [mediaError, setMediaError] = useState<MediaError>();
  const [cameraAvailable, setCameraAvailable] = useState(true);
  const [isActive, setIsActive] = useState(true); // Start active by default
  const lockerQuery = useGetLocker();
  const location = useLocation();
  const { lockerId } = getConfig();

  const isEnabled = Boolean(lockerQuery?.data?.config.webcam_enabled);
  const isHomePage = location.pathname === "/";
  const lockerIdentifier = lockerId || lockerQuery?.data?.id || "unknown";

  const capture = useCallback(() => {
    if (!isEnabled || !isActive || !cameraAvailable) return null;

    try {
      return webcamRef.current?.getScreenshot();
    } catch (error) {
      Sentry.captureException(error);
      return null;
    }
  }, [isEnabled, isActive, cameraAvailable]);

  const reportCameraError = useCallback(
    (error: MediaError) => {
      const cameraError = getCameraErrorName(error);
      const missingCamera = isMissingCameraError(error);

      if (missingCamera && reportedMissingCameraRef.current) return;
      if (missingCamera) {
        reportedMissingCameraRef.current = true;
        const attributes = {
          camera_error: cameraError,
          webcam_enabled: isEnabled,
          locker_id: lockerIdentifier,
        };

        recordIncrement("locker.camera.unavailable", 1, attributes);
        addBreadcrumb(
          "camera",
          "webcam enabled but no camera device was found",
          attributes,
          "warning"
        );
        return;
      }

      Sentry.withScope((scope) => {
        scope.setTag("camera_error", cameraError);
        scope.setTag("webcam_enabled", String(isEnabled));
        scope.setTag("locker_id", lockerIdentifier);
        scope.setLevel("error");
        Sentry.captureException(error ?? new Error("Unknown camera media error"));
      });
    },
    [isEnabled, lockerIdentifier]
  );

  const scheduleCameraRetry = useCallback(() => {
    if (!isEnabled) return;

    if (cameraRetryRef.current) {
      window.clearTimeout(cameraRetryRef.current);
    }

    cameraRetryRef.current = window.setTimeout(() => {
      cameraRetryRef.current = undefined;
      setCameraAvailable(true);
    }, CAMERA_RETRY_TIMEOUT);
  }, [isEnabled]);

  const handleUserMediaError = useCallback(
    (error: MediaError) => {
      setMediaError(error);

      if (isMissingCameraError(error)) {
        setCameraAvailable(false);
        scheduleCameraRetry();
      }

      reportCameraError(error);
    },
    [reportCameraError, scheduleCameraRetry]
  );

  // Handle user activity (keep minimal listeners on kiosks)
  useEffect(() => {
    const handleActivity = () => {
      if (isEnabled) {
        setIsActive(true);
      }
    };

    document.addEventListener("pointerdown", handleActivity);
    // document.addEventListener("keydown", handleActivity); Should be okay to remove
    if (process.env.NODE_ENV === "development") {
      document.addEventListener("mousemove", handleActivity);
    }

    return () => {
      document.removeEventListener("pointerdown", handleActivity);
      // document.removeEventListener("keydown", handleActivity);
      if (process.env.NODE_ENV === "development") {
        document.removeEventListener("mousemove", handleActivity);
      }
    };
  }, [isEnabled]);

  // Auto-stop camera on idle, but only if on home page
  useIdleTimer(CAMERA_IDLE_TIMEOUT, () => {
    if (isHomePage) {
      setIsActive(false);
    }
  });

  // Keep camera active on home page
  useEffect(() => {
    if (!isHomePage && isEnabled) {
      setIsActive(true);
    }
  }, [isHomePage, isEnabled]);

  useEffect(() => {
    if (isEnabled) return;

    if (cameraRetryRef.current) {
      window.clearTimeout(cameraRetryRef.current);
      cameraRetryRef.current = undefined;
    }
    setCameraAvailable(true);
  }, [isEnabled]);

  useEffect(() => {
    return () => {
      if (cameraRetryRef.current) {
        window.clearTimeout(cameraRetryRef.current);
      }
    };
  }, []);

  return (
    <CameraContext.Provider
      value={{
        capture,
        mediaError,
        isEnabled,
        cameraAvailable,
      }}>
      {children}

      {isEnabled && isActive && cameraAvailable && (
        <Webcam
          {...WEBCAM_CONFIG}
          ref={webcamRef}
          onUserMediaError={handleUserMediaError}
          mirrored={true}
        />
      )}
    </CameraContext.Provider>
  );
}

export const useCamera = () => {
  const context = useContext(CameraContext);
  if (!context) {
    throw new Error("useCamera must be used within a CameraProvider");
  }
  return context;
};
